/**
 * P0-21k — SSE 인증 정책 (review C-2 / interface §2.3).
 *
 * auth v1.5 cookie cutover:
 *   - fetch + `credentials: 'include'` → cookie 자동 동봉 (Authorization 헤더 의존 제거)
 *   - JWT 만료 1분 전 `: token-refresh-required` 코멘트 수신 시 refresh 호출 → cookie 갱신
 *   - 401/403 → onTokenRefreshRequired (refresh 후 재연결)
 *
 * 이전(v1.4): EventSource 금지 + fetch + Authorization Bearer 헤더 사용. cookie cutover 후
 * cookie 가 자동 동봉되므로 fetch 유지 + 헤더 부착만 제거 (EventSource 도 가능하지만 fetch
 * 가 더 풍부한 제어 — Last-Event-ID, 재연결, AbortController).
 *
 * Last-Event-ID SSOT (adr-20260428-sse-event-id-bigint-serialization):
 *   - SSE event id = chat_messages.commit_seq (BIGINT) 의 decimal string 형식
 *   - 본 모듈은 parsed.id 를 string 그대로 보존 — 서버가 BigInt → string 직렬화 보장
 *   - caller (P2-14 useSSE) 는 BigInt 비교 필요 시 `BigInt(lastEventId)` lazy 변환
 *
 * review CRIT-5 봉합:
 *   - 401/403 → onTokenRefreshRequired 즉시 호출 + backoff 리셋 + 재시도. refresh 실패 시 close.
 *   - EOF (정상 종료) → 즉시 재연결 대신 minimum 5s 지연 (thundering herd 회피).
 *   - attempt 가 cap 도달 → onDisconnect('exhausted') 후 close (영구 5분 재시도 차단).
 *
 * 본 모듈은 transport 만 책임. payload 파싱은 P2-13 / P2-14.
 */

import { startPresence, newClientId } from './presence';

export interface SseClientOptions {
  url: string;
  /**
   * push-presence-cellular — API base(예: '/api/v1'). 주면 SSE 수명 동안 presence 하트비트를 보낸다.
   * 미지정 시 하트비트 없음(서버가 이 사용자를 "오프라인" 으로 보고 모바일 푸시를 보낸다) — 상세: api/presence.ts.
   */
  presenceBaseUrl?: string;
  onEvent: (event: SseEvent) => void;
  /** 401/403 또는 token-refresh-required 코멘트 수신 시 호출. 갱신 실패 시 throw 또는 false 반환 권장. */
  onTokenRefreshRequired?: () => Promise<void> | void;
  onConnect?: () => void;
  onDisconnect?: (reason: SseDisconnectReason) => void;
  initialLastEventId?: string;
  /** 최대 재시도 횟수. 도달 시 close + onDisconnect('exhausted'). 기본 RECONNECT_BACKOFFS_MS.length. */
  maxAttempts?: number;
  /** refresh 연속 호출 cap (review NEW-HIGH-A). 도달 시 close. 기본 MAX_REFRESH_ATTEMPTS=3. */
  maxRefreshAttempts?: number;
}

export type SseDisconnectReason = 'eof' | 'exhausted' | 'unauthorized' | 'closed' | string;

export interface SseEvent {
  id?: string;
  event?: string;
  data: string;
  raw: string;
}

const RECONNECT_BACKOFFS_MS = [100, 1_000, 5_000, 30_000, 300_000] as const;
// EOF 정상 종료 후 최소 5s 지연 — 서버 재배포 시 thundering herd 회피 (review CRIT-5).
const EOF_MIN_BACKOFF_MS = 5_000;

export interface SseConnection {
  close: () => void;
}

// refresh 자체가 cap 우회 surface — 백엔드가 stale token 을 200/refresh 응답하면
// 401 → refresh OK → 401 → refresh OK 무한 루프. attempt 카운터를 0 으로 리셋하기 때문에
// maxAttempts cap 에 영원히 도달하지 못함 (review NEW-HIGH-A).
// 별도 refreshAttempts 카운터로 cap (기본 3회) 강제.
const MAX_REFRESH_ATTEMPTS = 3;

export function connectSse(opts: SseClientOptions): SseConnection {
  let lastEventId = opts.initialLastEventId ?? '';
  let attempt = 0;
  let refreshAttempts = 0;
  let closed = false;
  let abortController: AbortController | null = null;
  const maxAttempts = opts.maxAttempts ?? RECONNECT_BACKOFFS_MS.length;
  const maxRefreshAttempts = opts.maxRefreshAttempts ?? MAX_REFRESH_ATTEMPTS;

  // push-presence-cellular — 이 연결의 presence 식별자. 서버 connId 는 클라이언트에 노출되지 않으므로
  // 클라이언트가 생성한다. 서버는 연결 시 이 id 로 presence 를 등록하고, 정상 종료 시 반납한다.
  const clientId = newClientId();
  const streamUrl = opts.url + (opts.url.includes('?') ? '&' : '?') + `client_id=${encodeURIComponent(clientId)}`;
  // 하트비트는 SSE 수명과 같이 산다 — close() 에서 stop() → presence 즉시 반납 → 곧바로 푸시 전환.
  const presence = opts.presenceBaseUrl ? startPresence(opts.presenceBaseUrl, clientId) : null;

  // 401/403 시 refresh 시도 후 backoff 리셋. refresh 실패 → 영구 종료.
  // refresh 가 연속 maxRefreshAttempts 회 호출되면 (서버 영구 401) close — review NEW-HIGH-A.
  // 정상 200 응답이 한 번이라도 오면 refreshAttempts 가 리셋된다 (loop 내 reset).
  // 반환: true = 재시도 가능, false = 종료.
  async function tryRefreshAndContinue(): Promise<boolean> {
    if (!opts.onTokenRefreshRequired) {
      opts.onDisconnect?.('unauthorized');
      closed = true;
      return false;
    }
    refreshAttempts += 1;
    if (refreshAttempts > maxRefreshAttempts) {
      opts.onDisconnect?.('unauthorized');
      closed = true;
      return false;
    }
    try {
      await opts.onTokenRefreshRequired();
      attempt = 0; // refresh 성공 → backoff 리셋 (refreshAttempts 는 200 응답 시 리셋)
      return true;
    } catch (err) {
      opts.onDisconnect?.(err instanceof Error ? `unauthorized: ${err.message}` : 'unauthorized');
      closed = true;
      return false;
    }
  }

  async function loop(): Promise<void> {
    while (!closed) {
      abortController = new AbortController();
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      if (lastEventId) headers['Last-Event-ID'] = lastEventId;

      let normalEof = false;

      try {
        // auth v1.5 — credentials:'include' 로 cookie wc_at 자동 동봉.
        // streamUrl = opts.url + client_id (push-presence-cellular).
        const res = await fetch(streamUrl, {
          headers,
          credentials: 'include',
          signal: abortController.signal,
        });

        // CRIT-5: 401/403 분기 — backoff 영구 시도 차단, refresh 즉시 시도.
        if (res.status === 401 || res.status === 403) {
          const cont = await tryRefreshAndContinue();
          if (!cont) return;
          continue; // refresh 성공 — 즉시 재시도 (backoff 리셋됨)
        }

        // 429 분기 — chat-server slot map (MAX_CONCURRENT_PER_USER) 또는 global rate-limit.
        // 짧은 backoff (100ms~) 로 폭주하면 slot 이 release 되기 전에 또 시도해 영구 429.
        // Retry-After 헤더(초) 존중, 없으면 최소 30s 강제 + attempt 증가 → maxAttempts 도달 시
        // 'exhausted' 로 자연 종료. EventSource 폭주 storm 패턴 차단.
        if (res.status === 429) {
          const retryAfterSec = Number(res.headers.get('Retry-After'));
          const waitMs = Number.isFinite(retryAfterSec) && retryAfterSec > 0
            ? Math.min(retryAfterSec * 1000, 5 * 60 * 1000)
            : 30_000;
          opts.onDisconnect?.('rate_limited');
          if (closed) return;
          if (attempt >= maxAttempts) {
            opts.onDisconnect?.('exhausted');
            closed = true;
            return;
          }
          attempt += 1;
          await sleep(waitMs);
          continue;
        }

        if (!res.ok || !res.body) throw new Error(`SSE handshake failed: ${res.status}`);
        opts.onConnect?.();
        attempt = 0;
        refreshAttempts = 0; // 200 응답 1회 = healthy → refresh 카운터 리셋

        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            normalEof = true;
            break;
          }
          buffer += value;
          let sepIdx: number;
          while ((sepIdx = buffer.indexOf('\n\n')) >= 0) {
            const block = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);
            const parsed = parseEventBlock(block);
            if (parsed.id) lastEventId = parsed.id;
            if (parsed.tokenRefreshRequired) {
              const cont = await tryRefreshAndContinue();
              if (!cont) {
                try { abortController?.abort(); } catch { /* noop */ }
                return;
              }
              continue;
            }
            if (parsed.event !== undefined || parsed.data !== '') {
              opts.onEvent({ id: parsed.id, event: parsed.event, data: parsed.data, raw: block });
            }
          }
        }
        opts.onDisconnect?.('eof');
      } catch (err) {
        if (closed) return;
        opts.onDisconnect?.(err instanceof Error ? err.message : 'unknown');
      }

      if (closed) return;

      // attempt cap 도달 → 영구 5분 재시도 차단 (CRIT-5).
      if (attempt >= maxAttempts) {
        opts.onDisconnect?.('exhausted');
        closed = true;
        return;
      }

      // EOF 정상 종료는 최소 5s 지연 (thundering herd 회피). 그 외 에러는 backoff 매트릭스.
      const baseBackoff = RECONNECT_BACKOFFS_MS[Math.min(attempt, RECONNECT_BACKOFFS_MS.length - 1)]!;
      const backoff = normalEof ? Math.max(baseBackoff, EOF_MIN_BACKOFF_MS) : baseBackoff;
      attempt += 1;
      await sleep(backoff);
    }
  }

  void loop();

  return {
    close: () => {
      closed = true;
      // presence 즉시 반납 — TTL(60s) 대기 없이 곧바로 모바일 푸시로 전환.
      presence?.stop();
      abortController?.abort();
      opts.onDisconnect?.('closed');
    },
  };
}

interface ParsedBlock {
  id?: string;
  event?: string;
  data: string;
  tokenRefreshRequired: boolean;
}

// SSE 표준 (W3C Server-Sent Events): "field: value" 의 first space 1개만 제거.
// trim() 사용 시 leading/trailing space 가 의미있는 payload (multiline JSON pretty-print) 가
// 손상됨 (review HIGH "sseClient parseEventBlock").
function parseSseField(line: string): { field: string; value: string } | null {
  const colon = line.indexOf(':');
  if (colon < 0) return null;
  const field = line.slice(0, colon);
  let value = line.slice(colon + 1);
  // SSE spec: 첫 1개 space 만 제거 (있을 때).
  if (value.startsWith(' ')) value = value.slice(1);
  return { field, value };
}

function parseEventBlock(block: string): ParsedBlock {
  const lines = block.split('\n');
  let id: string | undefined;
  let event: string | undefined;
  const data: string[] = [];
  let tokenRefreshRequired = false;

  for (const line of lines) {
    if (line.startsWith(':')) {
      if (line.includes('token-refresh-required')) tokenRefreshRequired = true;
      continue;
    }
    const parsed = parseSseField(line);
    if (!parsed) continue;
    switch (parsed.field) {
      case 'id': id = parsed.value; break;
      case 'event': event = parsed.value; break;
      case 'data': data.push(parsed.value); break;
    }
  }

  return { id, event, data: data.join('\n'), tokenRefreshRequired };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
